import { Component, ComponentInterface, Element, Event, EventEmitter, h, State, Host, JSX, Listen, Prop } from '@stencil/core';
import { transformTagNames } from '../../../global/transform-tag-name';
import { isKeyboardClickOnFocusedElement } from '../../../utils/is-keyboard-click-on-focused-element';

/**
 * @slot - text content placed in the element
 * @slot icon - optional wb-icon placed before the content
 * @slot meta - optional content placed after the content
 * @slot meta-icon - optional wb-icon placed after the meta content
 * @slot button - optional button
 * @slot checkbox - optional checkbox to have instead of text content
 */
@Component({
  tag: 'wb-list-group-item',
  styleUrl: 'list-group-item.scss',
  shadow: true,
})
export class ListItemComponent implements ComponentInterface {
  @Element()
  public el: HTMLWbListGroupItemElement;

  /** makes element appear interactive */
  @Prop({ reflect: true })
  public interactive: boolean = false;

  /** makes element behave as link */
  @Prop({ reflect: true })
  public href: string;

  /** makes element obey the link target*/
  @Prop({ reflect: true })
  public target: string = '_self';

  /** sets custom tabIndex */
  @Prop({ reflect: true })
  public customTabindex: number;

  private TagNames = transformTagNames(this, 'wb-list-group-item', ['wb-text']);

  @State()
  public hasButton = false;

  @Listen('keydown')
  public handleKeyDown(event: KeyboardEvent): void {
    if ((this.interactive || this.href) && isKeyboardClickOnFocusedElement(this.el, event)) {
      event.preventDefault();
      if (this.href) {
        this.openHref();
      } else {
        this.el.click();
      }
    }
  }

  /** Emitted when the wb-list-group-item interactive receives click */
  @Event()
  public wblistgroupitemclick!: EventEmitter<void>;

  @Listen('click')
  public handleClick(event: MouseEvent): void {
    if (this.interactive) {
      this.wblistgroupitemclick.emit();
    }

    if (this.href) {
      event.preventDefault();
      this.openHref();
    }
  }

  private handleSlotChange = (event: Event): void => {
    const slot = event.target as HTMLSlotElement;
    slot.closest(this.TagNames.WbText)?.classList.toggle('no-slot-content', slot.assignedElements().length === 0);
  };

  private openHref() {
    window.open(this.href, this.target);
  }

  private getTabIndex() {
    if (this.customTabindex >= 0) {
      return this.customTabindex;
    }

    if (this.interactive || this.href) {
      return '0';
    }

    return undefined;
  }

  private handleButtonSlotChange = (event: Event) => {
    let slot = event.target as HTMLSlotElement;
    while (slot.assignedElements().length > 0) {
      const element = slot.assignedElements()[0];
      if (element instanceof HTMLSlotElement) {
        slot = element;
      } else {
        break;
      }
    }
    this.hasButton = slot.assignedElements().length > 0;
  };

  public render(): JSX.Element {
    const { WbText } = this.TagNames;

    return (
      <Host
        class={`wb-list-group-item ${this.hasButton ? 'has-button' : ''}`} //
        role={this.href ? 'link' : 'listitem'}
        href={this.href}
        tabindex={this.getTabIndex()}
      >
        <slot name="icon" />
        <slot name="button" onSlotchange={this.handleButtonSlotChange} />
        <WbText size="m" ellipsis>
          <slot />
        </WbText>
        <slot name="checkbox" />
        <WbText size="m" class="meta no-slot-content">
          <slot name="meta" onSlotchange={this.handleSlotChange} />
        </WbText>
        <slot name="meta-icon" />
      </Host>
    );
  }
}
